import RIOTable, { type TableStickyReference as RIOTableStickyReference } from '@rio-cloud/rio-uikit/Table';
import classnames from 'classnames';
import type React from 'react';

import DataTableBody from './DataTableBody';
import DataTableFooter from './DataTableFooter';
import DataTableHeader from './DataTableHeader';
import NoContent from './NoContent';
import styles from './styles/DataTable.module.scss';
import TableError from './TableError';
import TableSkeleton from './TableSkeleton';
import type { DataTableProps, DataTableStickyReference, RioTableRow, RowClassName, TableRowKey } from './types';

const toTableRowId = (key: React.Key): string | number => (typeof key === 'bigint' ? key.toString() : key);

const resolveRIOKey = <T extends object>(rowKey: TableRowKey<T>) =>
    typeof rowKey === 'function' ? (record: RioTableRow) => toTableRowId(rowKey(record as unknown as T)) : rowKey;

const resolveRowClassName = <T extends object>(
    rowClassName: RowClassName<T> | undefined,
    record: T,
    index: number
): string | undefined => (typeof rowClassName === 'function' ? rowClassName(record, index) : rowClassName);

const rioStickyReference: Record<DataTableStickyReference, RIOTableStickyReference> = {
    page: 'applicationLayout',
    container: 'container',
};

const DataTable = <T extends object>({
    'data-testid': dataTestId,
    columns,
    dataSource,
    rowKey,
    wrapperClassName,
    tableClassName,
    stickyHeader = false,
    stickyReference,
    maxHeight,
    headerClassName,
    rowClassName,
    loadMore,
    actionColumn,
    showSelectionColumn = false,
    selectedRowKeys,
    onSelectionChange,
    sortConfig,
    onSortChange,
    onRowClick,
    activeRowKey,
    isLoading = false,
    empty = {},
    isError = false,
    error = {},
}: DataTableProps<T>) => {
    if (isLoading) {
        return <TableSkeleton testId={`${dataTestId}-skeleton`} columns={columns} actionColumn={actionColumn} />;
    }
    if (isError) {
        return (
            <div className='width-100pct margin-top-15' data-testid={`${dataTestId}-error-wrapper`}>
                <TableError testId={`${dataTestId}-error`} {...error} />
            </div>
        );
    }
    if (dataSource.length === 0) {
        return <NoContent testId={`${dataTestId}-empty`} {...empty} />;
    }
    return (
        <div className={classnames('width-100pct display-flex flex-column', wrapperClassName)} data-testid={dataTestId}>
            <RIOTable<RioTableRow>
                className={classnames(styles.DataTable__table, 'width-100pct border-color-light', tableClassName)}
                rowKey={resolveRIOKey(rowKey)}
                stickyReference={stickyReference ? rioStickyReference[stickyReference] : undefined}
                showSelectionColumn={showSelectionColumn}
                selectedRowIds={selectedRowKeys?.map(toTableRowId) ?? []}
                onSelectionChange={keys => onSelectionChange?.(keys)}
                hover
                border={false}
                rounded={false}
                rowSeparatorWidth={0}
                activeRowId={activeRowKey === undefined ? undefined : toTableRowId(activeRowKey)}
                rowClassName={(record, index) => {
                    const resolvedRowKey = resolveRIOKey(rowKey);
                    const rowId =
                        typeof resolvedRowKey === 'function' ? resolvedRowKey(record) : record[resolvedRowKey];
                    const isActive = activeRowKey !== undefined && rowId === toTableRowId(activeRowKey);

                    return classnames(
                        styles.DataTable__row,
                        isActive && styles.DataTable__rowActive,
                        'width-100pct position-relative',
                        resolveRowClassName(rowClassName, record as unknown as T, index)
                    );
                }}
                sortBy={sortConfig?.key}
                sortDirection={sortConfig?.dir}
                onSortChange={key => onSortChange?.(key)}
                onRowClick={record => onRowClick?.(record as unknown as T)}
            >
                <DataTableHeader
                    columns={columns}
                    actionColumn={actionColumn}
                    className={headerClassName}
                    sticky={stickyHeader}
                />
                <DataTableBody
                    columns={columns}
                    dataSource={dataSource}
                    rowKey={rowKey}
                    actionColumn={actionColumn}
                    maxHeight={maxHeight}
                />
                {loadMore && <DataTableFooter loadMore={loadMore} />}
            </RIOTable>
        </div>
    );
};

export default DataTable;
